import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Gamepad2, 
  Shield, 
  Trophy, 
  Zap, 
  MessageCircle, 
  Phone, 
  Send,
  Home as HomeIcon,
  UserPlus,
  LogIn,
  Globe,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  Star,
  Instagram,
  BadgeCheck,
  ChevronDown,
  Download,
  Link2,
  TrendingUp,
  Gift,
  Clock,
  Wallet
} from 'lucide-react';
import { content, paymentMethods, gameGallery, reviews, socialMedia, howToGuides, faqData, rtpLive, promos } from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';

const iconMap = {
  gamepad2: Gamepad2,
  shield: Shield,
  trophy: Trophy,
  zap: Zap,
  messageCircle: MessageCircle,
  phone: Phone,
  send: Send
};

const Home = () => {
  const [language, setLanguage] = useState('id');
  const [isScrolled, setIsScrolled] = useState(false);
  const currentContent = content[language];
  const currentReviews = reviews[language];
  const currentHowTo = howToGuides[language];
  const currentFaq = faqData[language];
  const currentRtp = rtpLive[language];
  const currentPromos = promos[language];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'id' ? 'en' : 'id');
  };

  const LINKS = {
    register: 'https://x500.cc/daftar-leci123',
    login: 'https://x500.cc/login-leci123',
    liveChat: 'https://x500.cc/livechat-leci123',
    whatsapp: 'https://x500.cc/telegram-leci123',
    telegram: 'https://x500.cc/telegram-leci123'
  };

  const handleRegister = () => {
    window.open(LINKS.register, '_blank', 'noopener,noreferrer');
  };

  const handleLogin = () => {
    window.open(LINKS.login, '_blank', 'noopener,noreferrer');
  };

  const handleContact = (channel) => {
    const urlMap = {
      'Live Chat': LINKS.liveChat,
      'WhatsApp': LINKS.whatsapp,
      'Telegram': LINKS.telegram
    };
    const url = urlMap[channel] || LINKS.liveChat;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950">
      {/* Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-zinc-950/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a 
            href="/" 
            aria-label={language === 'id' ? 'Beranda LECI123' : 'LECI123 Home'}
            title="LECI123 - Situs Slot Online Terpercaya"
            className="flex items-center space-x-3 group transition-transform duration-300 hover:scale-105"
            data-testid="header-logo-link"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_leci-community/artifacts/hz36cpbw_logo.png" 
              alt="LECI123 - Situs Slot Online Terbaik Indonesia" 
              className="h-12 w-auto object-contain"
            />
            <span className="sr-only">LECI123</span>
          </a>
          <div className="flex items-center gap-2">
            <Button 
              onClick={toggleLanguage} 
              variant="outline" 
              size="sm"
              className="bg-zinc-800/50 border-orange-500/30 text-orange-400 hover:bg-orange-500/20 hover:text-orange-300 transition-all duration-300"
            >
              <Globe className="mr-2 h-4 w-4" />
              {language === 'id' ? 'EN' : 'ID'}
            </Button>
            <a
              href={socialMedia.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LECI123 Instagram"
              className="inline-flex items-center justify-center h-9 w-9 rounded-md bg-gradient-to-br from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white transition-all duration-300 transform hover:scale-105"
              data-testid="header-instagram-link"
            >
              <Instagram className="h-4 w-4" />
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="container mx-auto text-center relative z-10">
          <div className="mb-8 animate-fade-in">
            <a href="/" aria-label="LECI123" data-testid="hero-logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_leci-community/artifacts/hz36cpbw_logo.png" 
                alt="LECI123 - Situs Platform Slot Online Terbaik Indonesia" 
                className="h-32 w-auto mx-auto mb-6 drop-shadow-2xl"
              />
            </a>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-orange-400 via-red-500 to-orange-400 bg-clip-text text-transparent animate-fade-in" style={{ animationDelay: '0.2s' }}>
            {currentContent.hero.title}
          </h1>
          
          <p className="text-xl md:text-2xl text-zinc-300 mb-10 max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '0.4s' }}>
            {currentContent.hero.subtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <Button 
              size="lg" 
              onClick={handleRegister}
              className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-bold px-8 py-6 text-lg shadow-lg shadow-orange-500/50 hover:shadow-orange-500/70 transition-all duration-300 transform hover:scale-105"
            >
              <UserPlus className="mr-2 h-5 w-5" />
              {currentContent.hero.cta.register}
            </Button>
            <Button 
              size="lg" 
              onClick={handleLogin}
              variant="outline"
              className="border-2 border-orange-500 text-orange-400 hover:bg-orange-500/20 font-bold px-8 py-6 text-lg transition-all duration-300 transform hover:scale-105"
            >
              <LogIn className="mr-2 h-5 w-5" />
              {currentContent.hero.cta.login}
            </Button>
          </div>

          {/* Quick Navigation - SEO: Internal linking with keyword anchors */}
          <nav 
            className="mt-12 max-w-4xl mx-auto animate-fade-in" 
            aria-label={language === 'id' ? 'Navigasi cepat LECI123' : 'LECI123 quick navigation'}
            style={{ animationDelay: '0.8s' }}
            data-testid="quick-nav"
          >
            <p className="text-zinc-500 text-xs uppercase tracking-wider mb-3">
              {language === 'id' ? 'Akses Cepat' : 'Quick Access'}
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <a href="#cara-daftar" className="px-3 py-1.5 text-sm bg-zinc-800/60 border border-orange-500/20 text-orange-400 rounded-full hover:bg-orange-500/20 hover:border-orange-500/60 transition-all" data-testid="qnav-daftar">
                LECI123 Daftar
              </a>
              <a href="#cara-login" className="px-3 py-1.5 text-sm bg-zinc-800/60 border border-orange-500/20 text-orange-400 rounded-full hover:bg-orange-500/20 hover:border-orange-500/60 transition-all" data-testid="qnav-login">
                LECI123 Login
              </a>
              <a href="#apk-leci123" className="px-3 py-1.5 text-sm bg-zinc-800/60 border border-orange-500/20 text-orange-400 rounded-full hover:bg-orange-500/20 hover:border-orange-500/60 transition-all" data-testid="qnav-apk">
                LECI123 APK
              </a>
              <a href="#link-alternatif" className="px-3 py-1.5 text-sm bg-zinc-800/60 border border-orange-500/20 text-orange-400 rounded-full hover:bg-orange-500/20 hover:border-orange-500/60 transition-all" data-testid="qnav-link">
                LECI123 Link
              </a>
              <a href="#rtp-live" className="px-3 py-1.5 text-sm bg-zinc-800/60 border border-orange-500/20 text-orange-400 rounded-full hover:bg-orange-500/20 hover:border-orange-500/60 transition-all" data-testid="qnav-rtp">
                RTP Live LECI123
              </a>
              <a href="#promo" className="px-3 py-1.5 text-sm bg-zinc-800/60 border border-orange-500/20 text-orange-400 rounded-full hover:bg-orange-500/20 hover:border-orange-500/60 transition-all" data-testid="qnav-promo">
                {language === 'id' ? 'Bonus & Promo' : 'Bonus & Promo'}
              </a>
              <a href="#faq" className="px-3 py-1.5 text-sm bg-zinc-800/60 border border-orange-500/20 text-orange-400 rounded-full hover:bg-orange-500/20 hover:border-orange-500/60 transition-all" data-testid="qnav-faq">
                FAQ LECI123
              </a>
            </div>
          </nav>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-16 px-4 bg-zinc-900/50">
        <div className="container mx-auto max-w-4xl">
          <Card className="bg-zinc-800/50 border-orange-500/20 backdrop-blur-sm">
            <CardHeader>
              <div className="flex items-center justify-center mb-4">
                <Sparkles className="h-8 w-8 text-orange-500" />
              </div>
              <CardTitle className="text-3xl text-center text-orange-400">
                {currentContent.welcome.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-300 leading-relaxed text-center">
                {language === 'id' ? (
                  <>
                    Selamat datang di <strong className="text-orange-400">LECI123</strong>, situs platform slot online terpercaya yang dirancang khusus untuk pemain Indonesia. Anda dapat <a href="#cara-daftar" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300" data-testid="inline-link-daftar">daftar LECI123</a> hanya dalam 2 menit, atau langsung <a href="#cara-login" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300" data-testid="inline-link-login">login LECI123</a> jika sudah memiliki akun. Untuk pengguna mobile, tersedia <a href="#apk-leci123" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300" data-testid="inline-link-apk">LECI123 APK</a> yang lebih ringan dan hemat data. Jika domain utama diblokir, gunakan <a href="#link-alternatif" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300" data-testid="inline-link-alt">link alternatif LECI123</a> resmi kami.
                  </>
                ) : (
                  <>
                    Welcome to <strong className="text-orange-400">LECI123</strong>, a trusted online slot platform site designed specifically for Indonesian players. You can <a href="#cara-daftar" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300" data-testid="inline-link-daftar">register LECI123</a> in just 2 minutes, or <a href="#cara-login" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300" data-testid="inline-link-login">login to LECI123</a> if you already have an account. For mobile users, the <a href="#apk-leci123" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300" data-testid="inline-link-apk">LECI123 APK</a> is available. If the main domain is blocked, use our official <a href="#link-alternatif" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300" data-testid="inline-link-alt">LECI123 alternative link</a>.
                  </>
                )}
              </p>
              <p className="text-zinc-400 leading-relaxed text-center text-sm mt-4 italic">
                {language === 'id' ? (
                  <>
                    Pelajari lebih lanjut tentang dunia{' '}
                    <a
                      href="https://id.wikipedia.org/wiki/Permainan_daring"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-400 hover:text-orange-300 underline decoration-orange-500/40 hover:decoration-orange-400 transition-colors duration-300"
                      data-testid="wikipedia-permainan-daring-link"
                    >
                      permainan daring di Indonesia
                    </a>{' '}
                    dan perkembangannya hingga saat ini.
                  </>
                ) : (
                  <>
                    Learn more about the world of{' '}
                    <a
                      href="https://id.wikipedia.org/wiki/Permainan_daring"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-400 hover:text-orange-300 underline decoration-orange-500/40 hover:decoration-orange-400 transition-colors duration-300"
                      data-testid="wikipedia-permainan-daring-link"
                    >
                      online gaming in Indonesia
                    </a>{' '}
                    and how it has evolved over time.
                  </>
                )}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12 text-orange-400">
            {currentContent.features.title}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {currentContent.features.items.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <Card 
                  key={index} 
                  className="bg-zinc-800/50 border-orange-500/20 hover:border-orange-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-orange-500/20"
                >
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-gradient-to-br from-orange-600 to-red-600 rounded-lg">
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <CardTitle className="text-orange-400">{feature.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-zinc-300">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popular Games */}
      <section className="py-16 px-4 bg-zinc-900/50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-8 text-orange-400">
            {currentContent.games.title}
          </h2>
          <div className="flex flex-wrap gap-3 justify-center">
            {currentContent.games.items.map((game, index) => (
              <Badge 
                key={index} 
                variant="outline" 
                className="px-4 py-2 text-base bg-zinc-800/50 border-orange-500/30 text-orange-400 hover:bg-orange-500/20 transition-all duration-300 cursor-pointer"
              >
                {game}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery Section - For Google Images SEO */}
      <section className="py-20 px-4" data-testid="game-gallery-section">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-orange-400">
            {language === 'id' ? 'Galeri Game Slot LECI123' : 'LECI123 Slot Game Gallery'}
          </h2>
          <p className="text-center text-zinc-400 mb-12 max-w-2xl mx-auto">
            {language === 'id' 
              ? 'Pilihan game slot terbaik dengan RTP tinggi dan jackpot besar di LECI123' 
              : 'The best slot game choices with high RTP and big jackpots at LECI123'}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {gameGallery.map((game, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-xl border border-orange-500/20 hover:border-orange-500/60 transition-all duration-500 transform hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/30"
                data-testid={`game-card-${index}`}
              >
                <img
                  src={game.image}
                  alt={game.alt}
                  title={`${game.name} - LECI123`}
                  loading="lazy"
                  className="w-full h-48 md:h-64 object-cover transition-transform duration-700 group-hover:scale-110 bg-zinc-900"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                  <h3 className="text-orange-400 font-bold text-sm md:text-lg mb-1">{game.name}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-xs md:text-sm text-zinc-300">RTP {game.rtp}</span>
                    <Badge className="bg-orange-600/80 text-white text-xs">HOT</Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <Card className="bg-zinc-800/50 border-orange-500/20">
            <CardHeader>
              <div className="flex items-center justify-center mb-4">
                <Shield className="h-12 w-12 text-orange-500" />
              </div>
              <CardTitle className="text-3xl text-center text-orange-400">
                {currentContent.security.title}
              </CardTitle>
              <CardDescription className="text-center text-zinc-300 mt-4">
                {currentContent.security.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                {currentContent.security.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2 p-3 bg-zinc-900/50 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-zinc-300">{feature}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Payment Methods */}
      <section className="py-16 px-4 bg-zinc-900/50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-8 text-orange-400">
            {language === 'id' ? 'Metode Pembayaran' : 'Payment Methods'}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {paymentMethods.map((method, index) => (
              <div 
                key={index} 
                className="bg-zinc-800/50 border border-orange-500/20 rounded-lg p-4 text-center hover:border-orange-500/50 transition-all duration-300 transform hover:scale-105"
              >
                <p className="text-orange-400 font-semibold">{method.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <Card className="bg-zinc-800/50 border-orange-500/20">
            <CardHeader>
              <CardTitle className="text-3xl text-center text-orange-400">
                {currentContent.support.title}
              </CardTitle>
              <CardDescription className="text-center text-zinc-300 mt-4">
                {currentContent.support.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4 justify-center mt-6">
                {currentContent.support.channels.map((channel, index) => {
                  const IconComponent = iconMap[channel.icon];
                  return (
                    <Button
                      key={index}
                      onClick={() => handleContact(channel.name)}
                      className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-semibold transition-all duration-300 transform hover:scale-105"
                    >
                      <IconComponent className="mr-2 h-5 w-5" />
                      {channel.name}
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Reviews & Ratings Section - SEO Optimized */}
      <section className="py-20 px-4 bg-zinc-900/50" data-testid="reviews-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-orange-400">
              {currentReviews.title}
            </h2>
            <p className="text-zinc-400 mb-6">{currentReviews.subtitle}</p>
            
            {/* Aggregate Rating Display */}
            <div className="inline-flex items-center gap-3 bg-zinc-800/50 border border-orange-500/30 rounded-full px-6 py-3" data-testid="aggregate-rating">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star} 
                    className="h-5 w-5 fill-orange-400 text-orange-400" 
                  />
                ))}
              </div>
              <span className="text-2xl font-bold text-orange-400">
                {currentReviews.aggregateRating.ratingValue}
              </span>
              <span className="text-zinc-400 text-sm">
                / 5 ({currentReviews.aggregateRating.reviewCount.toLocaleString()} {language === 'id' ? 'ulasan' : 'reviews'})
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentReviews.items.map((review, index) => (
              <Card 
                key={index} 
                className="bg-zinc-800/50 border-orange-500/20 hover:border-orange-500/50 transition-all duration-300"
                data-testid={`review-card-${index}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center text-white font-bold">
                        {review.name.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-1">
                          <p className="text-orange-400 font-semibold text-sm">{review.name}</p>
                          {review.verified && (
                            <BadgeCheck className="h-4 w-4 text-blue-400" title={language === 'id' ? 'Member Terverifikasi' : 'Verified Member'} />
                          )}
                        </div>
                        <p className="text-zinc-500 text-xs">{review.location}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-4 w-4 ${
                          star <= review.rating 
                            ? 'fill-orange-400 text-orange-400' 
                            : 'text-zinc-600'
                        }`}
                      />
                    ))}
                    <span className="text-zinc-500 text-xs ml-2">{review.date}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-zinc-300 text-sm leading-relaxed">&ldquo;{review.comment}&rdquo;</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* RTP Live Section - SEO: freshness + transactional intent */}
      <section className="py-20 px-4" data-testid="rtp-live-section" id="rtp-live">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-green-600/20 border border-green-500/40 text-green-400 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              <TrendingUp className="h-4 w-4" />
              LIVE NOW
            </div>
            <h2 className="text-4xl font-bold mb-3 text-orange-400">{currentRtp.title}</h2>
            <p className="text-zinc-400">{currentRtp.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentRtp.games.map((game, index) => {
              const statusColor = game.status === 'GACOR' || game.status === 'HOT' 
                ? 'text-green-400 bg-green-500/10 border-green-500/30' 
                : game.status === 'WARM' 
                ? 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30'
                : 'text-blue-400 bg-blue-500/10 border-blue-500/30';
              return (
                <div key={index} className="bg-zinc-800/50 border border-orange-500/20 rounded-xl p-4 hover:border-orange-500/50 transition-all" data-testid={`rtp-${index}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-orange-400 text-sm">{game.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColor}`}>{game.status}</span>
                  </div>
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-bold text-white">{game.rtp}</span>
                    <span className="text-zinc-400 text-sm pb-1">%</span>
                  </div>
                  <div className="mt-2 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-orange-500 to-red-500" 
                      style={{ width: `${game.rtp}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How To Daftar - SEO: HowTo schema + keyword cluster */}
      <section className="py-20 px-4 bg-zinc-900/50" data-testid="howto-daftar-section" id="cara-daftar">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold text-center mb-3 text-orange-400">{currentHowTo.daftar.title}</h2>
          <p className="text-center text-zinc-400 mb-12 max-w-2xl mx-auto">{currentHowTo.daftar.description}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {currentHowTo.daftar.steps.map((step, index) => (
              <Card key={index} className="bg-zinc-800/50 border-orange-500/20 hover:border-orange-500/50 transition-all duration-300 relative" data-testid={`daftar-step-${index}`}>
                <div className="absolute -top-3 -left-3 h-10 w-10 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white font-bold shadow-lg shadow-orange-500/40">
                  {index + 1}
                </div>
                <CardContent className="pt-8 pb-6">
                  <h3 className="font-bold text-orange-400 mb-2">{step.name}</h3>
                  <p className="text-zinc-300 text-sm leading-relaxed">{step.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button 
              onClick={handleRegister}
              size="lg"
              className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-bold px-8 py-6 text-lg shadow-lg shadow-orange-500/50"
              data-testid="howto-daftar-cta"
            >
              <UserPlus className="mr-2 h-5 w-5" />
              {language === 'id' ? 'Daftar Sekarang' : 'Register Now'}
            </Button>
          </div>
        </div>
      </section>

      {/* How To Login - SEO: Login keyword */}
      <section className="py-20 px-4" data-testid="howto-login-section" id="cara-login">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold text-center mb-3 text-orange-400">{currentHowTo.login.title}</h2>
          <p className="text-center text-zinc-400 mb-12 max-w-2xl mx-auto">{currentHowTo.login.description}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {currentHowTo.login.steps.map((step, index) => (
              <Card key={index} className="bg-zinc-800/50 border-orange-500/20 hover:border-orange-500/50 transition-all duration-300 relative" data-testid={`login-step-${index}`}>
                <div className="absolute -top-3 -left-3 h-10 w-10 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white font-bold shadow-lg shadow-orange-500/40">
                  {index + 1}
                </div>
                <CardContent className="pt-8 pb-6">
                  <h3 className="font-bold text-orange-400 mb-2">{step.name}</h3>
                  <p className="text-zinc-300 text-sm leading-relaxed">{step.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button 
              onClick={handleLogin}
              size="lg"
              variant="outline"
              className="border-2 border-orange-500 text-orange-400 hover:bg-orange-500/20 font-bold px-8 py-6 text-lg"
              data-testid="howto-login-cta"
            >
              <LogIn className="mr-2 h-5 w-5" />
              {language === 'id' ? 'Login Sekarang' : 'Login Now'}
            </Button>
          </div>
        </div>
      </section>

      {/* Link Alternatif & APK Section - SEO: 2 keywords in 1 section */}
      <section className="py-20 px-4 bg-zinc-900/50" data-testid="link-apk-section">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Link Alternatif */}
            <Card className="bg-zinc-800/50 border-orange-500/20" data-testid="link-alternatif-card" id="link-alternatif">
              <CardHeader>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-3 bg-gradient-to-br from-orange-600 to-red-600 rounded-lg">
                    <Link2 className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-orange-400">
                    {language === 'id' ? 'Link Alternatif LECI123' : 'LECI123 Alternative Link'}
                  </CardTitle>
                </div>
                <CardDescription className="text-zinc-300">
                  {language === 'id' 
                    ? 'Daftar link alternatif LECI123 resmi untuk akses anti Nawala dan anti blokir ISP. Pastikan hanya menggunakan link resmi untuk menghindari phising.'
                    : 'Official LECI123 alternative links for anti-Nawala and anti-ISP-block access. Make sure to only use official links to avoid phishing.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <a 
                  href="https://x500.cc/daftar-leci123" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-zinc-900/50 border border-orange-500/20 rounded-lg hover:border-orange-500/60 transition-all group"
                  data-testid="alternatif-link-1"
                >
                  <span className="text-orange-400 font-mono text-sm">Alternatif 1</span>
                  <Badge className="bg-green-600/80 text-white text-xs">ACTIVE</Badge>
                </a>
                <a 
                  href="https://x500.cc/login-leci123" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-zinc-900/50 border border-orange-500/20 rounded-lg hover:border-orange-500/60 transition-all group"
                  data-testid="alternatif-link-2"
                >
                  <span className="text-orange-400 font-mono text-sm">Alternatif 2</span>
                  <Badge className="bg-green-600/80 text-white text-xs">ACTIVE</Badge>
                </a>
                <p className="text-zinc-500 text-xs italic mt-3">
                  <Clock className="inline h-3 w-3 mr-1" />
                  {language === 'id' ? 'Diperbarui: Hari ini' : 'Updated: Today'}
                </p>
              </CardContent>
            </Card>

            {/* APK Download */}
            <Card className="bg-zinc-800/50 border-orange-500/20" data-testid="apk-download-card" id="apk-leci123">
              <CardHeader>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-3 bg-gradient-to-br from-orange-600 to-red-600 rounded-lg">
                    <Download className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-orange-400">
                    {language === 'id' ? 'Download LECI123 APK' : 'Download LECI123 APK'}
                  </CardTitle>
                </div>
                <CardDescription className="text-zinc-300">
                  {language === 'id' 
                    ? 'Aplikasi LECI123 APK resmi untuk Android — lebih ringan, hemat data, dan tetap aman seperti versi web. Cocok untuk koneksi internet terbatas.'
                    : 'Official LECI123 APK app for Android — lighter, data-saving, and equally safe as the web version. Suitable for limited internet connections.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-zinc-900/50 p-2 rounded">
                    <span className="text-zinc-500">Versi:</span>
                    <span className="text-orange-400 font-semibold ml-2">v3.2.1</span>
                  </div>
                  <div className="bg-zinc-900/50 p-2 rounded">
                    <span className="text-zinc-500">Ukuran:</span>
                    <span className="text-orange-400 font-semibold ml-2">12 MB</span>
                  </div>
                  <div className="bg-zinc-900/50 p-2 rounded">
                    <span className="text-zinc-500">OS:</span>
                    <span className="text-orange-400 font-semibold ml-2">Android 5+</span>
                  </div>
                  <div className="bg-zinc-900/50 p-2 rounded">
                    <span className="text-zinc-500">Update:</span>
                    <span className="text-orange-400 font-semibold ml-2">2026</span>
                  </div>
                </div>
                <a
                  href="https://x500.cc/download-leci123"
                  rel="noopener noreferrer"
                  className="w-full mt-3 inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-bold rounded-md transition-all duration-300 transform hover:scale-[1.02]"
                  data-testid="apk-download-cta"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {language === 'id' ? 'Download Sekarang' : 'Download Now'}
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bonus & Promo Section */}
      <section className="py-20 px-4" data-testid="promo-section" id="promo">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-orange-400">{currentPromos.title}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {currentPromos.items.map((promo, index) => (
              <Card key={index} className="bg-gradient-to-br from-zinc-800/80 to-zinc-900/80 border-orange-500/30 hover:border-orange-500/60 transition-all duration-300" data-testid={`promo-${index}`}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-gradient-to-br from-orange-600 to-red-600 rounded-lg">
                      <Gift className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-xl text-orange-400">{promo.name}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-zinc-300 text-sm">{promo.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section - SEO Critical: FAQPage Schema + Featured Snippet */}
      <section className="py-20 px-4 bg-zinc-900/50" data-testid="faq-section" id="faq">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold text-center mb-3 text-orange-400">{currentFaq.title}</h2>
          <p className="text-center text-zinc-400 mb-12">
            {language === 'id' 
              ? 'Jawaban untuk pertanyaan paling sering ditanyakan seputar LECI123'
              : 'Answers to the most frequently asked questions about LECI123'}
          </p>
          <Accordion type="single" collapsible className="w-full space-y-3" data-testid="faq-accordion">
            {currentFaq.items.map((item, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-zinc-800/50 border border-orange-500/20 rounded-lg px-5 hover:border-orange-500/40 transition-colors"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left text-orange-400 hover:text-orange-300 font-semibold hover:no-underline py-4">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-300 leading-relaxed pb-4">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* About LECI123 Brand Section - Brand Authority Reinforcement */}
      <section className="py-16 px-4" data-testid="about-brand-section" id="tentang-leci123">
        <div className="container mx-auto max-w-4xl">
          <Card className="bg-gradient-to-br from-zinc-900/80 to-zinc-800/40 border-orange-500/30">
            <CardHeader>
              <div className="flex items-center justify-center mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_leci-community/artifacts/hz36cpbw_logo.png" 
                  alt="LECI123 Brand Logo"
                  className="h-20 w-auto"
                />
              </div>
              <CardTitle className="text-3xl text-center text-orange-400">
                {language === 'id' ? 'Tentang LECI123' : 'About LECI123'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-300 leading-relaxed text-center mb-4">
                {language === 'id' ? (
                  <>
                    <strong className="text-orange-400">LECI123</strong> adalah brand situs slot online terkemuka di Indonesia yang berdedikasi memberikan pengalaman bermain terbaik bagi setiap pemain. Sebagai platform terpercaya, <a href="/" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300" data-testid="brand-link-home-1">LECI123</a> menggabungkan teknologi keamanan modern dengan beragam game slot premium dari provider ternama dunia.
                  </>
                ) : (
                  <>
                    <strong className="text-orange-400">LECI123</strong> is a leading online slot platform brand in Indonesia dedicated to providing the best gaming experience for every player. As a trusted platform, <a href="/" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300" data-testid="brand-link-home-1">LECI123</a> combines modern security technology with various premium slot games from renowned world providers.
                  </>
                )}
              </p>
              <p className="text-zinc-300 leading-relaxed text-center mb-6">
                {language === 'id' ? (
                  <>
                    Brand <a href="/" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300" data-testid="brand-link-home-2">LECI123</a> dipercaya oleh lebih dari 12.000 member aktif yang menikmati kemudahan akses melalui <a href="#link-alternatif" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300">link alternatif LECI123</a> resmi, kemudahan instalasi via <a href="#apk-leci123" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300">aplikasi LECI123 APK</a>, serta layanan pelanggan profesional yang tersedia 24/7.
                  </>
                ) : (
                  <>
                    The <a href="/" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300" data-testid="brand-link-home-2">LECI123</a> brand is trusted by more than 12,000 active members who enjoy easy access through official <a href="#link-alternatif" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300">LECI123 alternative links</a>, easy installation via the <a href="#apk-leci123" className="text-orange-400 underline decoration-orange-500/40 hover:text-orange-300">LECI123 APK app</a>, and professional 24/7 customer service.
                  </>
                )}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <div className="bg-zinc-800/50 rounded-lg p-4 border border-orange-500/20">
                  <div className="text-2xl font-bold text-orange-400">12K+</div>
                  <div className="text-xs text-zinc-400 mt-1">{language === 'id' ? 'Member Aktif' : 'Active Members'}</div>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-4 border border-orange-500/20">
                  <div className="text-2xl font-bold text-orange-400">500+</div>
                  <div className="text-xs text-zinc-400 mt-1">{language === 'id' ? 'Game Slot' : 'Slot Games'}</div>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-4 border border-orange-500/20">
                  <div className="text-2xl font-bold text-orange-400">24/7</div>
                  <div className="text-xs text-zinc-400 mt-1">{language === 'id' ? 'Customer Service' : 'Customer Service'}</div>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-4 border border-orange-500/20">
                  <div className="text-2xl font-bold text-orange-400">96%+</div>
                  <div className="text-xs text-zinc-400 mt-1">{language === 'id' ? 'RTP Tinggi' : 'High RTP'}</div>
                </div>
              </div>
              <div className="text-center mt-6">
                <a 
                  href="/" 
                  className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold underline decoration-orange-500/40"
                  data-testid="brand-back-home-link"
                >
                  <HomeIcon className="h-4 w-4" />
                  {language === 'id' ? 'Kembali ke Beranda LECI123' : 'Back to LECI123 Home'}
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Warning Section */}
      <section className="py-16 px-4 bg-zinc-900/50">
        <div className="container mx-auto max-w-4xl">
          <Card className="bg-amber-950/30 border-amber-500/50">
            <CardHeader>
              <div className="flex items-center justify-center mb-4">
                <AlertTriangle className="h-10 w-10 text-amber-500" />
              </div>
              <CardTitle className="text-2xl text-center text-amber-400">
                {currentContent.warning.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-amber-200/80 text-center">
                {currentContent.warning.description}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 border-t border-orange-500/20 py-8 px-4 mb-20 md:mb-0">
        <div className="container mx-auto text-center">
          <a href="/" aria-label="LECI123" className="inline-block" data-testid="footer-logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_leci-community/artifacts/hz36cpbw_logo.png" 
              alt="LECI123 - Situs Slot Online Terpercaya Indonesia" 
              className="h-16 w-auto mx-auto mb-4"
            />
          </a>
          <p className="text-zinc-400 mb-4">
            <a href="/" className="text-orange-400 font-semibold hover:underline" data-testid="footer-brand-link">LECI123</a>
            {' — '}
            {currentContent.footer.description}
          </p>
          
          {/* Social Media Links */}
          <div className="flex items-center justify-center gap-3 mb-4" data-testid="footer-social-links">
            <a
              href={socialMedia.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LECI123 Instagram - Follow us"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-medium text-sm transition-all duration-300 transform hover:scale-105"
              data-testid="footer-instagram-link"
            >
              <Instagram className="h-4 w-4" />
              <span>@leci123.website</span>
            </a>
          </div>
          
          <Separator className="my-4 bg-orange-500/20" />
          <p className="text-zinc-500 text-sm">{currentContent.footer.copyright}</p>
        </div>
      </footer>

      {/* Fixed Bottom Navigation (Mobile) */}
      <nav className="fixed bottom-0 left-0 right-0 bg-zinc-950/95 backdrop-blur-md border-t border-orange-500/20 md:hidden z-50">
        <div className="grid grid-cols-4 gap-1 p-2">
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex flex-col items-center justify-center py-2 text-orange-400 hover:text-orange-300 transition-colors duration-300"
          >
            <HomeIcon className="h-6 w-6 mb-1" />
            <span className="text-xs">Home</span>
          </button>
          <button 
            onClick={handleRegister}
            className="flex flex-col items-center justify-center py-2 text-orange-400 hover:text-orange-300 transition-colors duration-300"
          >
            <UserPlus className="h-6 w-6 mb-1" />
            <span className="text-xs">{language === 'id' ? 'Daftar' : 'Register'}</span>
          </button>
          <button 
            onClick={handleLogin}
            className="flex flex-col items-center justify-center py-2 text-orange-400 hover:text-orange-300 transition-colors duration-300"
          >
            <LogIn className="h-6 w-6 mb-1" />
            <span className="text-xs">Login</span>
          </button>
          <button 
            onClick={() => handleContact('Live Chat')}
            className="flex flex-col items-center justify-center py-2 text-orange-400 hover:text-orange-300 transition-colors duration-300"
          >
            <MessageCircle className="h-6 w-6 mb-1" />
            <span className="text-xs">Chat</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Home;
